import { Dtos, type ProConId, type SuretyAccountId } from "@inrev/common";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import { proConsQueryKey, retiredProConsQueryKey } from "../decisioning.keys";
import { type MutationFailureAction } from "../mutationFailure";
import { replaceById } from "../optimistic";
import { bucketSize } from "../rules";
import { type Factor, type ProCon } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export type UpdateProConInput = {
	id: ProConId;
	changes: Dtos.Decisioning.ProCon.Update.Request;
	/** Embedded catalog element for optimistic UI when `changes.factorId` changes the element. */
	factor?: Factor;
};

/** Mirrors the server's update rules for the optimistic patch: importance change moves the
 * item to the bottom of its new bucket, and a set value becomes the underwriter's. */
const toOptimisticPatch = (
	existing: ProCon,
	items: readonly ProCon[],
	{ retired: _retired, ...changes }: Dtos.Decisioning.ProCon.Update.Request,
	factor: Factor | undefined,
): Partial<ProCon> => {
	const patch: Partial<ProCon> = { ...changes, updatedAt: DateTime.now() };
	if (changes.factorId !== undefined && factor) patch.factor = factor;
	// Dropping an override shows the old value until the server reads the snapshot's.
	if (changes.observedValue === null) patch.observedValue = existing.observedValue;
	else if (changes.observedValue !== undefined) patch.observedValueSource = "underwriter";
	if (changes.importance !== undefined && changes.importance !== existing.importance) {
		patch.rank = bucketSize(items, existing.kind, changes.importance, existing.id);
	}
	return patch;
};

type UpdateProConOptions = {
	/**
	 * Offered with the failure toast, e.g. reopening the sheet with what was typed. Edits apply
	 * as soon as they are saved, so without it a failed edit's changes are lost.
	 */
	failureAction?: (input: UpdateProConInput) => MutationFailureAction | undefined;
};

export const useUpdateProCon = (
	accountId: SuretyAccountId,
	{ failureAction }: UpdateProConOptions = {},
) => {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useOptimisticListMutation<ProCon, UpdateProConInput, ProCon>({
		queryKey: proConsQueryKey(accountId),
		request: async ({ id, changes }) =>
			Dtos.Decisioning.ProCon.Get.Response.schema.parse(
				await patch<unknown>(`/v2/admin/surety/accounts/${accountId}/pro-cons/${id}`, changes),
			),
		apply: (items, { id, changes, factor }) => {
			if (changes.retired) return items.filter((item) => item.id !== id);
			const existing = items.find((item) => item.id === id);
			if (!existing) return items;
			const optimisticPatch = toOptimisticPatch(existing, items, changes, factor);
			return items.map((item) => (item.id === id ? { ...item, ...optimisticPatch } : item));
		},
		affectedIds: ({ id }) => [id],
		failureMessage: ({ changes }) =>
			changes.retired ? "Couldn't retire the factor." : "Couldn't save the factor.",
		failureAction,
		reconcile: (items, updated) => {
			if (updated.retiredAt === null) return replaceById(items, updated.id, updated);
			queryClient.setQueryData<ProCon[]>(
				retiredProConsQueryKey(accountId),
				(retired) => retired && [updated, ...retired.filter((item) => item.id !== updated.id)],
			);
			return items.filter((item) => item.id !== updated.id);
		},
	});

	return {
		updateProCon: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		updateProConAsync: mutation.mutateAsync,
	};
};
